import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  FileText, 
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  X,
  Download,
  Upload,
  User,
  MapPin,
  GraduationCap,
  DollarSign
} from 'lucide-react';
import { Application } from '../../types';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import AddApplicationModal from '../Modals/AddApplicationModal';
import { useAuth } from '../../contexts/AuthContext';

const mockApplications: Application[] = [
  {
    id: 'app-1',
    studentId: 'student-1',
    studentName: 'Zeynep Kara',
    country: 'İtalya',
    university: 'Politecnico di Milano',
    program: 'Bilgisayar Mühendisliği',
    degree: 'Yüksek Lisans',
    startDate: new Date('2024-09-01'),
    applicationFee: 100,
    tuitionFee: 15000,
    status: 'submitted',
    submittedAt: new Date('2024-01-15'),
    documents: ['passport', 'transcript', 'language-certificate', 'cv'],
    submittedBy: 'Fatma Özkan',
    notes: 'Öğrenci mühendislik alanında çok başarılı. Kabul şansı yüksek.',
    requirements: ['IELTS 6.5', 'Lisans Diploması', 'Motivasyon Mektubu', 'CV'],
    deadline: new Date('2024-03-15')
  },
  {
    id: 'app-2',
    studentId: 'student-2',
    studentName: 'Ahmet Yıldız',
    country: 'Almanya',
    university: 'Technical University of Munich',
    program: 'İşletme Yönetimi',
    degree: 'Lisans',
    startDate: new Date('2024-10-01'),
    applicationFee: 75,
    tuitionFee: 0,
    status: 'under-review',
    submittedAt: new Date('2024-01-10'),
    documents: ['passport', 'transcript', 'language-certificate'],
    submittedBy: 'Fatma Özkan',
    notes: 'Almanya\'da ücretsiz eğitim fırsatı. Dil seviyesi yeterli.',
    requirements: ['IELTS 6.0', 'Lise Diploması', 'Almanca B2'],
    deadline: new Date('2024-04-30')
  },
  {
    id: 'app-3',
    studentId: 'student-3',
    studentName: 'Elif Demir',
    country: 'İspanya',
    university: 'Universidad Complutense Madrid',
    program: 'Hukuk',
    degree: 'Lisans',
    startDate: new Date('2024-09-15'),
    applicationFee: 50,
    tuitionFee: 8000,
    status: 'accepted',
    submittedAt: new Date('2024-01-05'),
    responseDate: new Date('2024-01-20'),
    documents: ['passport', 'transcript', 'motivation-letter'],
    submittedBy: 'Can Kaya',
    notes: 'Başvuru kabul edildi! Öğrenci çok mutlu.',
    requirements: ['IELTS 6.5', 'Lise Diploması', 'Motivasyon Mektubu'],
    deadline: new Date('2024-02-28'),
    acceptanceLetter: 'Kabul mektubu alındı'
  },
  {
    id: 'app-4',
    studentId: 'student-4',
    studentName: 'Can Özkan',
    country: 'İtalya',
    university: 'Università Bocconi',
    program: 'İşletme Yönetimi',
    degree: 'Yüksek Lisans',
    startDate: new Date('2024-09-01'),
    applicationFee: 150,
    tuitionFee: 18000,
    status: 'rejected',
    submittedAt: new Date('2024-01-08'),
    responseDate: new Date('2024-01-25'),
    documents: ['passport', 'transcript', 'language-certificate', 'cv', 'motivation-letter'],
    submittedBy: 'Fatma Özkan',
    notes: 'Maalesef başvuru reddedildi. Alternatif üniversiteler önerildi.',
    requirements: ['IELTS 7.0', 'Lisans Diploması', 'GMAT', 'İş Deneyimi'],
    deadline: new Date('2024-02-15'),
    rejectionReason: 'GMAT puanı yetersiz'
  },
  {
    id: 'app-5',
    studentId: 'student-5',
    studentName: 'Merve Yılmaz',
    country: 'İtalya',
    university: 'Università di Bologna',
    program: 'Mimarlık',
    degree: 'Lisans',
    startDate: new Date('2024-09-01'),
    applicationFee: 80,
    tuitionFee: 12000,
    status: 'waitlisted',
    submittedAt: new Date('2024-01-12'),
    documents: ['passport', 'transcript', 'portfolio', 'motivation-letter'],
    submittedBy: 'Fatma Özkan',
    notes: 'Bekleme listesinde. Portföy çok güçlü.',
    requirements: ['IELTS 6.0', 'Lise Diploması', 'Portföy', 'Motivasyon Mektubu'],
    deadline: new Date('2024-03-01')
  }
];

const Applications: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [applications, setApplications] = useState<Application[]>(mockApplications);
  const [showAddModal, setShowAddModal] = useState(false);

  const getStatusColor = (status: Application['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'submitted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'under-review': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'accepted': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'waitlisted': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusText = (status: Application['status']) => {
    switch (status) {
      case 'draft': return 'Taslak';
      case 'submitted': return 'Gönderildi';
      case 'under-review': return 'İnceleniyor';
      case 'accepted': return 'Kabul Edildi';
      case 'rejected': return 'Reddedildi';
      case 'waitlisted': return 'Bekleme Listesi';
      default: return status;
    }
  };

  const filteredApplications = mockApplications.filter(app => {
    const matchesSearch = 
      app.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.university.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.program.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.country.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesCountry = countryFilter === 'all' || app.country === countryFilter;
    
    return matchesSearch && matchesStatus && matchesCountry;
  });

  const handleSaveApplication = (applicationData: any) => {
    const newApplication: Application = {
      ...applicationData,
      id: `app-${Date.now()}`,
      submittedAt: new Date(),
      submittedBy: user?.name || 'Sistem',
      documents: [],
      status: 'draft'
    };
    
    setApplications(prev => [newApplication, ...prev]);
    setShowAddModal(false);
    
    // Başarı bildirimi göster
    console.log('Yeni başvuru eklendi:', newApplication);
  };

  const handleApplicationAction = (action: string, applicationId: string) => {
    console.log(`Action: ${action} for application: ${applicationId}`);
    // Burada gerçek uygulamada API çağrıları yapılacak
  };

  const getStats = () => {
    const total = applications.length;
    const underReview = applications.filter(app => app.status === 'under-review').length;
    const accepted = applications.filter(app => app.status === 'accepted').length;
    const rejected = applications.filter(app => app.status === 'rejected').length;
    
    return { total, underReview, accepted, rejected };
  };

  const stats = getStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Başvurular</h2>
          <p className="text-gray-600 dark:text-gray-400">
            {user?.role === 'unit' && user?.unitType === 'education_consultant' 
              ? 'Danışmanlık yaptığınız öğrencilerin başvurularını yönetin'
              : 'Tüm üniversite başvurularını yönetin'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Başvuru</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <FileText className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Başvuru</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <Clock className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.underReview}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">İnceleniyor</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.accepted}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Kabul Edildi</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.rejected}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Reddedildi</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Öğrenci, üniversite, program ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="draft">Taslak</option>
            <option value="submitted">Gönderildi</option>
            <option value="under-review">İnceleniyor</option>
            <option value="accepted">Kabul Edildi</option>
            <option value="rejected">Reddedildi</option>
            <option value="waitlisted">Bekleme Listesi</option>
          </select>

          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Tüm Şehirler</option>
            <option value="İtalya">İtalya</option>
            <option value="Milano">Milano</option>
            <option value="Roma">Roma</option>
            <option value="Bologna">Bologna</option>
            <option value="Firenze">Firenze</option>
          </select>

          <button className="flex items-center justify-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2.5 rounded-lg transition-colors">
            <Filter className="w-4 h-4" />
            <span>Filtrele</span>
          </button>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Öğrenci</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Üniversite</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Program</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Şehir</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Durum</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Başvuru Tarihi</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Başlangıç</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Danışman</th>
                <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredApplications.map((application) => (
                <tr key={application.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-semibold">
                          {application.studentName.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{application.studentName}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">ID: {application.studentId}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{application.university}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{application.degree}</p>
                      {application.applicationFee > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-500">Başvuru: €{application.applicationFee}</p>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <span className="text-gray-900 dark:text-white">{application.program}</span>
                      {application.tuitionFee > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">€{application.tuitionFee.toLocaleString()}/yıl</p>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-900 dark:text-white">İtalya</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                      {getStatusText(application.status)}
                    </span>
                    {application.deadline && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Deadline: {format(application.deadline, 'dd MMM', { locale: tr })}
                      </p>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">
                        {application.submittedAt ? format(application.submittedAt, 'dd MMM yyyy', { locale: tr }) : '-'}
                      </span>
                      {application.responseDate && (
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          Cevap: {format(application.responseDate, 'dd MMM', { locale: tr })}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className="text-gray-600 dark:text-gray-400">
                      {format(application.startDate, 'MMM yyyy', { locale: tr })}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-900 dark:text-white">{application.submittedBy}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end space-x-2">
                      <button 
                        onClick={() => handleApplicationAction('view', application.id)}
                        className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Detayları Görüntüle"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleApplicationAction('edit', application.id)}
                        className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="Düzenle"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleApplicationAction('download', application.id)}
                        className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                        title="Belgeleri İndir"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredApplications.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-gray-500 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Başvuru bulunamadı</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Arama kriterlerinizi değiştirmeyi deneyin veya yeni bir başvuru ekleyin
          </p>
        </div>
      )}

      {/* Add Application Modal */}
      <AddApplicationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveApplication}
      />
    </div>
  );
};

export default Applications;